import type { ReactNode } from 'react';
import ContactPopover from './ContactPopover';
import type { InlineContact, ContactContext } from '@/lib/contactTypes';
import type { OutboundLinkEvidence } from '@/lib/outboundSignature';

/**
 * The single way a real person's name is rendered anywhere in the app.
 *
 * Screens pass whatever loose shape they already have (name / email / phone /
 * company) and this normalises it into an `InlineContact` for the popover, so
 * no screen hand-rolls contact markup. Rule: any place a real person's name is
 * displayed, it is a `ContactName`.
 *
 * When there is nothing to contact (no company id, and no email/phone), it
 * renders plain text — no dead trigger.
 */
export interface ContactNameProps {
  /** Loose person shape. Any of these may be missing. */
  person: {
    user_id?: string | null;
    id?: string | null;
    full_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    company_id?: string | null;
    company_name?: string | null;
    contact_source?: InlineContact['contact_source'];
  } | null | undefined;
  /** Falls back to the company of the surface when the row has none. */
  companyId?: string | null;
  companyName?: string | null;
  context?: ContactContext;
  sourceSurface?: string;
  /** Evidence used to decide where an outbound signature link may point. */
  linkEvidence?: OutboundLinkEvidence | null;
  /** Override the visible label (defaults to the resolved display name). */
  children?: ReactNode;
  className?: string;
  /** Rendered when there is no name at all. */
  fallbackLabel?: string;
}

export const resolveDisplayName = (person: ContactNameProps['person'], fallback = 'Unnamed'): string => {
  if (!person) return fallback;
  const joined = [person.first_name, person.last_name].filter(Boolean).join(' ').trim();
  return (
    person.full_name?.trim() ||
    person.name?.trim() ||
    (joined || null) ||
    person.email?.trim() ||
    fallback
  );
};

export const toInlineContact = (
  person: ContactNameProps['person'],
  companyId?: string | null,
  companyName?: string | null,
  fallbackLabel = 'This person',
): InlineContact | null => {
  if (!person) return null;
  const resolvedCompanyId = person.company_id ?? companyId ?? null;
  const email = person.email?.trim() || null;
  const phone = person.phone?.trim() || null;

  // Nothing actionable: no in-app target and no external channel.
  if (!resolvedCompanyId && !email && !phone) return null;

  return {
    user_id: person.user_id ?? person.id ?? null,
    full_name: resolveDisplayName(person, fallbackLabel),
    email,
    phone,
    // ContactPopover requires a company id for the in-app message dialog; when
    // there is none we still surface email/phone channels.
    company_id: resolvedCompanyId ?? '',
    company_name: person.company_name ?? companyName ?? null,
    contact_source: person.contact_source ?? 'first_member',
  };
};

export default function ContactName({
  person,
  companyId,
  companyName,
  context,
  sourceSurface,
  linkEvidence,
  children,
  className,
  fallbackLabel = 'Unnamed',
}: ContactNameProps) {
  const label = children ?? resolveDisplayName(person, fallbackLabel);
  const contact = toInlineContact(person, companyId, companyName, fallbackLabel);

  if (!contact) {
    return <span className={className}>{label}</span>;
  }

  return (
    <ContactPopover
      contact={contact}
      context={context}
      sourceSurface={sourceSurface}
      linkEvidence={linkEvidence}
    >
      <span
        role="button"
        tabIndex={0}
        className={`cursor-pointer underline decoration-dotted underline-offset-2 hover:text-primary ${className ?? ''}`}
      >
        {label}
      </span>
    </ContactPopover>
  );

}
